import { z } from 'zod'
import { listQuerySchema } from './lists.js'

/**
 * Permessi dell'area riservata, per risorsa: `view` (lista e scheda in sola lettura) e `manage`
 * (crea, modifica, elimina; include `view`). Una pagina di sola consultazione (la Panoramica) ha solo `view`.
 * Nuova voce del menu = nuova risorsa qui.
 */
export const PERMISSIONS = {
  overview: ['view'],
  users: ['view', 'manage'],
  groups: ['view', 'manage'],
  requests: ['view', 'manage'],
  tournaments: ['view', 'manage'],
  matches: ['view', 'manage'],
} as const

export const PERMISSION_ACTIONS = ['view', 'manage'] as const

type Permissions = typeof PERMISSIONS
export type PermissionResource = keyof Permissions
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number]
export type PermissionKey = { [R in PermissionResource]: `${R}.${Permissions[R][number]}` }[PermissionResource]

/** Risorse con lista e scheda modificabili (hanno `manage`). */
export type CrudResource = {
  [R in PermissionResource]: 'manage' extends Permissions[R][number] ? R : never
}[PermissionResource]

export const PERMISSION_RESOURCES = Object.keys(PERMISSIONS) as PermissionResource[]

export const PERMISSION_KEYS = PERMISSION_RESOURCES.flatMap((resource) =>
  PERMISSIONS[resource].map((action) => `${resource}.${action}` as PermissionKey),
) as [PermissionKey, ...PermissionKey[]]

export const PermissionKeySchema = z.enum(PERMISSION_KEYS)

export const isPermissionKey = (value: string): value is PermissionKey =>
  (PERMISSION_KEYS as readonly string[]).includes(value)

/** `granted` concede `key`? `x.manage` include `x.view`. */
export function hasPermission(granted: readonly string[], key: PermissionKey): boolean {
  if (granted.includes(key)) return true
  const [resource, action] = key.split('.')
  return action === 'view' && granted.includes(`${resource}.manage`)
}

/** Permessi di `wanted` che `granted` non concede: nessuno può dare più di quanto ha. */
export const missingPermissions = (wanted: readonly PermissionKey[], granted: readonly string[]) =>
  wanted.filter((key) => !hasPermission(granted, key))

/** Gruppo di sistema (migrazione 0003): ha sempre tutti i permessi e non si elimina. */
export const SYSTEM_GROUP_ID = 'administrators'

/** POST /api/groups, PUT /api/groups/:id — sul gruppo di sistema i permessi sono ignorati. */
export const GroupRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
  permissions: z.array(PermissionKeySchema).max(PERMISSION_KEYS.length),
})

export type GroupRequest = z.infer<typeof GroupRequestSchema>

export type AdminGroup = {
  id: string
  name: string
  isSystem: boolean
  permissions: PermissionKey[]
  memberCount: number
  createdAt: string
}

/** GET /api/groups/options: per le select di utenti e filtri. */
export type GroupOption = { id: string; name: string }

/** GET /api/groups: `permission` = gruppi che concedono quel permesso. */
export const GroupListQuerySchema = listQuerySchema(['name', 'members', 'createdAt'], {
  permission: PermissionKeySchema.optional(),
})

export type GroupListQuery = z.infer<typeof GroupListQuerySchema>

/** GET /api/me */
export type MeResponse = { permissions: PermissionKey[] }
